package org.multiplatform.kotfolio.pages.kotlin

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.FontSize
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.silk.components.text.SpanText
import org.jetbrains.compose.web.css.em
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Ul
import org.multiplatform.kotfolio.components.KotlinCode
import org.multiplatform.kotfolio.layout.BlogLayout
import org.multiplatform.kotfolio.layout.BlogProperties
import org.multiplatform.kotfolio.layout.PageProperties

@Page("/kotlin/filter_collection")
@Composable
fun FilterCollectionPage() {
    BlogLayout(
        PageProperties(
            "Filter Collection",
            "first(), firstOrNull(), singleOrNull(), mapOf(), map(), associateBy(), associateWith(), associate(), flatMap(), distinct(), groupBy(), sortedBy(), sortedByDescending(), sortedWith(), scan(), scanReduce(), Collection builders(), filter(), filterNot(), filterIndexed(), filterIsInstance(), filterNotNull(), mapNotNull(), chunked(), windowed(), partition(), cantChangeList(), getOrNull(), getValue(), getOrDefault(), getOrElse(), binarySearch(), binarySearchBy(), binarySearch(), binarySearchBy(), distinct(), set(), any(), none(), all(), zipWithNext(), intersect(), runningFold(), coerceAtLeast(minValue), coerceIn(minValue, maxValue), coerceAtMax, coerceAtMost(maxValue), elementAt(), elementAtOrElse(), elementAtOrNull(), sumOf(), maxByOrNull(), minByOrNull(), toInt(), toIntOrNull(), takeUnless(), takeIf()"
        ),
        BlogProperties("kotlin", "Filter Collection")
    ) {
        Column(
            horizontalAlignment = Alignment.Start
        ) {
            SpanText(
                text = "Filtering Collections",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            SpanText(
                text = "plus(), minus()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    listOf(1,2,3) + 4 //[1,2,3,4]
                    
                    listOf(1,2,3) - 2 //[1,3]
                    """.trimIndent()
            )

            SpanText(
                text = "getData()",
                modifier = Modifier.fontSize(FontSize.Large).padding(top = 2.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """ 
                    getData()?.let { view.showData(it) } ?: view.showError()
                """.trimIndent()
            )

            SpanText(
                text = "first() vs firstOrNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "The functions first() and firstOrNull() serve the purpose of fetching the initial element of a collection or a sequence. Their distinction lies in their behavior when operating on empty collections or sequences.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )

                Ul(Modifier.margin(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "first()",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Li {
                        SpanText(
                            text = "This function retrieves the initial element of a collection or sequence, and if the collection or sequence is empty, it raises a NoSuchElementException.",
                            modifier = Modifier.padding(topBottom = 0.2.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }

                Ul(Modifier.margin(topBottom = 1.em).toAttrs()) {
                    SpanText(
                        text = "firstOrNull()",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                            .fontWeight(FontWeight.Bold)
                    )

                    Li {
                        SpanText(
                            text = "This function retrieves the initial element of a collection or sequence. If the collection or sequence is empty, it returns null.",
                            modifier = Modifier.padding(topBottom = 0.2.em)
                                .fontSize(FontSize.Medium)
                        )
                    }
                }
            }

            KotlinCode(
                code = """
                                 val numbers = listOf(5, 6, 7, 8, 9)

                                 val firstNumber = numbers.first()
                                 println(firstNumber) // Output: 5
                            """.trimIndent()
            )

            KotlinCode(
                code = """
                                val numbers = emptyList<Int>()

                                val firstNumber = numbers.firstOrNull()
                                println(firstNumber) // Output: null
                            """.trimIndent()
            )

            SpanText(
                text = "singleOrNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "The singleOrNull function returns the single element of the collection, or null if the collection is empty or has more than one element. It’s useful when you expect the collection to contain at most one element that matches certain criteria.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                 val names = listOf("Alice")
                                 val singleName = names.singleOrNull()
                                 println(singleName) // Output: Alice

                                 val multipleNames = listOf("Alice", "Bob", "Charlie")
                                 val singleNameInMultiple = multipleNames.singleOrNull() // Returns null as the list has more than one element
                                 println(singleNameInMultiple) // Output: null
                            """.trimIndent()
            )

            SpanText(
                text = "mapOf()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, “mapOf” is a function that allows you to create an immutable map, also known as’a dictionary or associative array, with a specified set of key-value pairs.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                  val myMap = mapOf(
                                    "key1" to "value1",
                                    "key2" to "value2",
                                    "key3" to "value3")
                            """.trimIndent()
            )

            KotlinCode(
                code = """
                                 val readOnlyMap = mapOf("name" to "Ashfaq", "age" to 28)

                                 for ((key, value) in readOnlyMap) {

                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "map()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, the map function is used to transform the elements of a collection. It applies a given transformation function to each element in the collection and returns a new collection containing the transformed elements.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    fun main () {
                        val numbers = listOf(1, 2, 3, 4, 5)
                        val squaredNumbers = numbers.map{ it * it }
                        println(squaredNumbers)
                    }
                """.trimIndent()
            )

            SpanText(
                text = "associateBy() - (one key -> one value)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "You want the list items to be the Values of the map, and you need to select a Key. In Kotlin, you can use the associateBy function to convert a List into a Map by specifying a key for each item in the list. This function creates a map where each item in the list is associated with a key generated by a lambda function. associateBy has an overload where you can also transform the value, but the primary logic remains that the iteration drives the key selection.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                data class User(val id: Int, val name: String)

                                val userList = listOf(
                                    User(1, "Alice"),
                                    User(2, "Bob"),
                                    User(3, "Charlie")
                                )

                                // Convert List to Map with ID as the key
                                val userMap = userList.associateBy { it.id }

                                println(userMap)
                                //{1=User(id=1, name=Alice), 2=User(id=2, name=Bob), 3=User(id=3, name=Charlie)}
                            """.trimIndent()
            )

            SpanText(
                text = "associateWith()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "You want the list items to be the Keys of the map, and you need to generate a Value. This is useful when you have a list of “keys” (like a list of IDs or unique names) and you want to generate data for them (like fetching a status, checking a permission, or setting a default value).",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                 // Result type: Map<User, Boolean>
                                 val userActiveStatus = userList.associateWith { user ->
                                     checkIfActive(user.id) // Imaginary function returning true/false
                                 }
                                 // Output: {User(id=1...)=true, User(id=2...)=false, ...}
                            """.trimIndent()
            )

            SpanText(
                text = "associate()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "You need complete control over both the Key and the Value. associate is the most generic of the three. It expects you to return a Pair<Key, Value>. This gives you flexibility but comes with a slight performance cost because it creates temporary Pair objects for every item in the list. You define a custom mapping for both. You don’t want the whole User object. associate creates short-lived Pair objects, if performance is critical in a tight loop, prefer associateBy (with value selector) or manually populating a MutableMap. For general usage, associate is perfectly fine. You just want a Map mapping the User’s Name to their Role.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                 // Result type: Map<String, String>
                                 val nameToRole = userList.associate { it.name to it.role }

                                 println(nameToRole)
                                 // Output: {Alice=Admin, Bob=Editor, Charlie=Viewer}
                            """.trimIndent()
            )


            SpanText(
                text = "flatten()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Used when working with nested collections.",
                    modifier = Modifier.padding(topBottom = 0.2.em).fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    val result = listOf(
                        listOf(1, 2),
                        listOf(3, 4)
                    ).flatten()
                    
                    [1, 2, 3, 4]
                    """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun findOutOfStockItem(orders: List<List<String>>): String? {
                        return orders.flatten().firstOrNull { it == "out_of_stock" }
                    }
                    """.trimIndent()
            )

            SpanText(
                text = "flatMap()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "The flatMap function transforms each element of the collection into a collection of elements, and then flattens the result into a single collection. Use flatMap when you need to transform each element into multiple elements and flatten the result.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                val words = listOf("hello", "world")
                                val characters = words.flatMap { it.toList() } // Convert each word to a list of characters
                                println(characters) // Output: [h, e, l, l, o, w, o, r, l, d]
                            """.trimIndent()
            )

            SpanText(
                text = "distinct()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "The distinct function returns a new collection containing only distinct elements from the original collection. Use distinct when you need to remove duplicate elements from a collection.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    val numbersWithDuplicates = listOf(1, 2, 3, 1, 2, 4, 5, 3)
                    val uniqueNumbers = numbersWithDuplicates.distinct() // Remove duplicates
                    println(uniqueNumbers) // Output: [1, 2, 3, 4, 5]
                """.trimIndent()
            )

            SpanText(
                text = "distinct() vs toSet()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "distinct(): Removes duplicates but keeps list form. When you need to keep order or return a list. Slightly slower (explicit duplicate check). Uses a LinkedHashSet internally (same performance).",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "toSet(): Converts list to a Set (automatically removes duplicates). When you need unique elements and don’t care about order. When you need to keep order or return a list. Removes duplicates efficiently using hashing.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "groupingBy() + eachCount() - (Character frequency, Duplicate detection, Word count, Analytics)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "Creates frequency counts.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val counts = nums.groupingBy { it }.eachCount()
                    """.trimIndent()
            )

            SpanText(
                text = "groupBy() - (one key -> many values)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "For groupBy, this means they will be added to the same list. While for associateBy , as it stores one value per key.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                                //GroupBy with total length
                                data class Filter(
                                    val value: String = "",
                                    val total: Int = 0
                                )

                                 fun main()
                                 {
                                     fun groupBy(products: List<String>): Set<Filter> {
                                         return products.groupBy { it }.map { entry ->
                                             Filter(entry.key, entry.value.size)
                                         }.toSet()
                                     }

                                     println(groupBy(listOf("Flutter", "Flutter", "Jetpack Compose", "Kotlin", "KMM")))
                                 }

                                 //Grouping Elements by Parity with groupBy
                                 fun main() {
                                     val numbers = listOf(1, 2, 3, 4, 5, 6)
                                     val groupedByParity = numbers.groupBy {
                                         if(it % 2 == 0) "Even" else "Odd"
                                     }
                                     println(groupedByParity)
                                 }

                                 //Counting Elements with groupingBy
                                 fun main() {
                                     val letters = listOf("a", "b", "a", "c", "b")
                                     val letterCounts = letters.groupingBy { it }.eachCount()
                                     println(letterCounts)
                                 }

                                 //Grouping Words by Initial Letter with groupByTo
                                 fun main() {
                                     val words = listOf("apple", "banana", "blueberry")
                                     val groupedWords = mutableMapOf<Char, MutableList<String>>()

                                     words.groupByTo(groupedWords) { it.first() }
                                     println(groupedWords)
                                 }
                            """.trimIndent()
            )

            KotlinCode(
                code = """
                                fun main(){

                                    val cities = mutableListOf("Nagpur","Mumbai","Pune","Nashik","Nagpur","Mumbai","Pune")

                                    //1st approach groupingBy
                                    val cityCount = cities.groupingBy { it }.eachCount()
                                    println(cityCount)

                                    //2nd approach using Map - Using LinkedHashMap it helps to maintain the order
                                    val map: MutableMap<String, Int> = LinkedHashMap()
                                    for(i in cities){
                                        var count = map[i]
                                        if (count == null) count =0
                                        map[i] = count+1
                                        println(map)
                                    }

                                    //3rd approach - mutableMap
                                    val count = mutableMapOf<String, Int>()

                                    cities.forEach { city ->
                                        val a = count[city] ?: 0
                                        count[city] = a + 1
                                    }
                                    println(count)
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "sortedBy()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Takes a selector function and sorts the objects based on that property in ascending order.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "sortedByDescending()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Same as above, but in reverse.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                 data class Product(val name: String, val price: Double)

                                 // Sample list of products
                                 val products = listOf(
                                     Product("Laptop", 1200.0),
                                     Product("Smartphone", 800.0),
                                     Product("Tablet", 500.0),
                                     Product("Smartwatch", 300.0)
                                 )

                                 // Sort products by price in ascending order
                                 val sortedByPriceAsc = products.sortedBy { it.price }

                                 // Sort products by price in descending order
                                 val sortedByPriceDesc = products.sortedByDescending { it.price }
                            """.trimIndent()
            )

            SpanText(
                text = "sortedWith()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "sortedWith() function to sort collections based on a custom comparator. This is particularly useful when we want to define specific sorting criteria for the data.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                data class Person(val age: Int, val name:String)

                                fun main() {

                                    val people = listOf(
                                        Person (30, "Alice"),
                                        Person (25, "Bob"),
                                        Person (35, "Charlie")
                                    )

                                    // Sort by age
                                    val sortedByAge = people.sortedWith(compareBy { it.age })
                                    println(sortedByAge)

                                    // Sort by name
                                    val sortedByName = people.sortedWith(compareBy { it. name })
                                    println(sortedByName)

                                    // Sort by age, then by name
                                    val sortedByAgeThenName = people.sortedWith(
                                    compareBy<Person> { it.age }.thenBy { it.name })
                                    println(sortedByAgeThenName)

                                    people.sortedWith(compareBy({it.age}, {it.name})).forEach{ println(it) }
                                }
                            """.trimIndent()
            )

            KotlinCode(
                code = """
                                fun main() {
                                    val people = listOf(
                                        Person("Alice", 30),
                                        Person("Bob", 25),
                                        Person("Charlie", 35),
                                        Person("Bob", 35)
                                    )

                                    // Sort by age in descending order, then by name in ascending order
                                    val customSorted = people
                                                      .sortedWith(
                                                          compareByDescending<Person>
                                                          { it.age }.thenBy { it.name }
                                                       )
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "scan()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "scan() is similar to fold() as both carry out a binary operation to a list of values. The difference is that scan() returns a list containing transitional results, while fold() returns only the final result.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            SpanText(
                text = "scanReduce()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "scanReduce() function is similar to the reduce() function and can be used when you want the first element as an initial value. As with scan(), the scanReduce() also returns a list of transitional results.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    /**
                    * Transforms the encapsulated value using the given [transform] function which returns a [Result],
                    * or returns the original failure if this instance represents a failure.
 
                    * @param T The incoming value type.
                    * @param R The resulting value type.
                    * @param transform The transformation producing another [Result].
                    * @return The result of [transform] if this was success, or a failure.
                    */
 
                    inline fun <T, R> Result<T>.flatMap(transform: (T) -> Result<R>): Result<R> =
                        fold(
                            onSuccess = { transform(it) },
                            onFailure = { Result.failure(it) },
                        )
                """.trimIndent()
            )


            KotlinCode(
                code = """
                                fun main() {
                                    val list = listOf(1,3,5,6)
                                    println(list.scan(0){sum, acc -> sum+acc}) //[0, 1, 4, 9, 15]
                                    println(list.fold(0){sum, acc -> sum+acc})//15
                                }
                                
                                fun main() {
                                    val list = listOf(5,9,15,20)
                                    println(list.scanReduce{sum, acc -> sum+acc}) //[5, 14, 29, 49]
                                    println(list.reduce{sum, acc -> sum+acc})//49
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "Collection builders",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Some other cool features are the additions to builder functions for collections. Apart from buildString, we now have buildList, buildSet, and buildMap. These builder functions can be used to manipulate an immutable collection and return a read-only result. The buildSet takes a lambda with a receiver argument (“this”). Mutable actions can be performed on the argument, while the buildSet returns an immutable Set as result. So basically, with builder functions, we can add and remove stuff and do other kinds of manipulations on an immutable collection.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                fun main() {
                                    val set = setOf(3, 4, 56, 21)
                                    val result = buildSet {
                                        //add 33 to the new set
                                        add(33)
                                        set.mapTo(this) { it * 2 }
                                    }
                                    print(result) //[33, 6, 8, 112, 42]
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "filter() / filterNot()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, “filter” is a higher-order function that operates on collections (such as lists, arrays, or other iterable data structures) and is used to create a new collection containing elements that satisfy a specified condition. This function is commonly use",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                 fun main () {
                                     val numbers = listOf(1, 2, 3, 4, 5, 6, 7, 8, 9, 10)
                                     val evenNumbers = numbers.filter { it % 2 == 0}
                                     val notMultiples0f3 = numbers.filterNot { number -> number % 3 == 0 }

                                     println("Even numbers)
                                     println(evenNumbers)

                                     println(""Numbers not multiples of 3")
                                     println(notMultiples0f3)
                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "filterIndexed()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, the filterIndexed function is used to filter the elements of a collection (such as a list) based on their indices and values. It takes a lambda function as an argument that specifies the filtering criteria.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    fun main () {
                        val numbers = listOf(10, 20, 30, 40, 50, 60, 70, 80, 90, 100)
                        val filteredNumbers = numbers.filterIndexed { index, value ->
                            index % 2 == 0 && value > 50
                        }
                        
                        println("Filtered numbers:")
                        println(filteredNumbers)
                    }
                            """.trimIndent()
            )

            SpanText(
                text = "filterIsInstance()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "The filterlsInstance() is used to filter elements of a collection based on their type. It allows you to extract elements of a specific type from a mixed-type collection. This function is especially useful when you have a collection containing different types of elements and you want to retrieve only the elements that match a certain type.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                fun main () {
                                    val mixedList = listOf('a', 1, 'b', 2, 'c', 3)
                                    val letters = mixedList.filterIsInstance<Char>()

                                    println("Letters:")
                                    println(letters) // Output: Letters: [a, b, c]
                                    val numbers = mixedList.filterIsInstance<Int>()

                                     println ("Numbers:")
                                    println (numbers) // Output: Numbers: [1, 2, 3]
                                }
                            """.trimIndent()
            )

            KotlinCode(
                code = """
                                 open class EducationalMaterial(val title: String)
                                 class Book(title: String, val author: String) : EducationalMaterial(title)
                                 class Video(title: String, val duration: Int) : EducationalMaterial(title) // Duration in minutes class Article(title: String, val publishedDate: String) : EducationalMaterial(title)

                                 fun main() {
                                     //A list containing different types of educational materials
                                     val materials = listOf(
                                         Book("Kotlin", "John Doe"),
                                         Video("Start Kotlin", 120),
                                         Book("Android", "Jane Smith"),
                                         Video("Advance Kotlin", 90),
                                     )

                                     val videos = materials.filterIsInstance<Video>()

                                     videos.forEach { video -> println(video.title) }
                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "filterNotNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "filterNotNull function is used to filter out and remove any null elements from a collection. This function is helpful when you have a collection that may contain both non-null and nullable elements, and you want to obtain a new collection that includes only the non-null elements.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                fun main () {
                                    val mixedList = listOf(1, null, "Hello", null, 3.14, "World", null)
                                    val nonNullElements = mixedList.filterNotNull()

                                    println("Non-null elements:")
                                    println(nonNullElements)
                                }

                                fun main() {
                                    val numbers = listOfNotNull(1, null, 2, null, 3)
                                    println(numbers)
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "mapNotNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "mapNotNull() transforms elements and removes null values automatically.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    val list = listOf("1", "2", "abc", "4")
                    val result = list.mapNotNull { it.toIntOrNull() }
                    println(result)
                """.trimIndent()
            )

            SpanText(
                text = "chunked()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Breaks a list into smaller chunks.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                  val numbers = (1..10).toList()
                                  val result = numbers.chunked(3)
                                  
                                  [[1,2,3],[4,5,6],[7,8,9],[10]]
                            """.trimIndent()
            )

            SpanText(
                text = "windowed()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Creates sliding window subsets.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                  val numbers = listOf(1,2,3,4)
                                  val result = numbers.windowed(2)
                                  
                                  [[1,2],[2,3],[3,4]]
                            """.trimIndent()
            )

            SpanText(
                text = "partition()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, the partition() is used to split a collection into two separate lists based on a given predicate. It divides the original collection into two parts: one containing elements that satisfy the specified predicate, and the other containing elements that do not.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    val numbers = listOf(1, 2, 3, 4, 5, 6)
                    val (even, odd) = numbers.partition { it % 2 == 0 }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    val adults = people.filter { it.age >= 18 }
                    val minors = people.filter { it.age < 18 }
                    
                    val (adults, minors) = people.partition { it.age >= 18 }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                                 public inline fun <T> Array<out T>.partition(predicate: (T) -> Boolean): Pair<List<T>, List<T>> {
                                     val first = ArrayList<T>()
                                     val second = ArrayList<T>()
                                     for (element in this) {
                                         if (predicate(element)) {
                                             first.add(element)
                                         } else {
                                             second.add(element)
                                         }
                                     }
                                     return Pair(first, second)
                                 }

                                 data class Student(val id: Int, val name: String, val isStudent: Boolean)

                                 fun main() {
                                     val studentArray = arrayOf(
                                     Student(1, "Ganesh", true),
                                     Student(2, "Ram", false),
                                     Student(5, "Rajesh", false),
                                     Student(5, "Ramesh", true),
                                     Student(3, "Rahul", false))

                                     val (students, notStudents) = studentArray.partition { it.isStudent }
                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "cantChangeList() / getOrNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "We can reach a list item using its place or index.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    var cantChangeList = listOf<Int>(1, 2, 3)
                    val firstItem = cantChangeList[0] 
                    println(firstItem) //Output: 1
                                 
                    val cantChangeList = listOf(1, 2, 3)
                    val safeItem = cantChangeList.getOrNull(0) // Safely get the first item
                    println(safeItem) // Output: 1, or null if index 0 doesn't exist
                """.trimIndent()
            )

            SpanText(
                text = "getValue() / getOrDefault() / getOrElse() - (Retrieving values from a Kotlin map)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "getValue: To access values in a Map, you can use multiple methods, let’s start the easiest one that it’s using associated key similar to accessing any element in an array.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "getOrDefault: This method is particularly useful to avoid NullPointerException. It returns a default value if the key is not present in the map. In the following example, if the key is not found, it will return 0.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "getOrElse: his method is similar to getOrDefault(), but instead of providing the default value, you can us a lambda expression to allow more complex cases.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                                val devAges = mapOf<String, Int>("Anupam" to 34, "Singh" to 24, "Aleix" to 44)
                                val anupamAge = devAges.getValue("Anupam")
                                println(anupamAge)
                                
                                val devAges = mapOf<String, Int>("Anupam" to 34, "Singh" to 24, "Aleix" to 44)
                                val anupamAge = devAges.getOrDefault("Anupam", 0)
                                println(anupamAge)
                                
                                val devAges = mapOf<String, Int>("Anupam" to 34, "Singh" to 24, "Aleix" to 44)
                                val anupamAge = devAges.getOrElse("Anupam") { 0 }
                                println(anupamAge)
                            """.trimIndent()
            )

            KotlinCode(
                code = $$"""
                                val result = runCatching {
                                    "kamlesh".toInt()   // will throw NumberFormatException
                                }

                                val number = result.getOrElse { exception ->
                                    println("Error: ${exception.message}")
                                    0 // fallback value
                                }

                                println(number) // Output: 0
                            """.trimIndent()
            )

            SpanText(
                text = "binarySearch() / binarySearchBy() - (Depend Sorting Order)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "binarySearch: Use this method when you have a list of simple values (like Integers or Strings) or objects that implement Comparable. It cuts the search space in half repeatedly. If the element is found, it returns the index. If not found, it returns a negative number indicating where the element should be.", modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "binarySearchBy: Usually, you have a list of Objects (like a User or Product), and you want to search based on a specific property (like an ID or username). While you could write a custom Comparator for the standard binarySearch, Kotlin gives us a cleaner way: binarySearchBy. It allows you to define a selector function to extract the value you want to compare.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = $$"""
                                //binarySearch
                                fun main() {
                                    // 1. Create a sorted list
                                    val scores = listOf(10, 20, 30, 40, 50, 60, 70)

                                    // 2. Search for an existing element
                                    val indexFound = scores.binarySearch(40)
                                    println("Found 40 at index: $indexFound") // Output: 3
                                    // 3. Search for a missing element
                                    val indexMissing = scores.binarySearch(45)
                                    println("Result for 45: $indexMissing") // Output: -5
                                }
                                
                                //binarySearchBy
                                data class User(val id: Int, val name: String)

                                fun main() {
                                    // List MUST be sorted by the property we are searching (id)
                                    val users = listOf(
                                        User(101, "Alice"),
                                        User(102, "Bob"),
                                        User(105, "Charlie"),
                                        User(109, "Diana")
                                    )
                                    // Search for the User with ID 105
                                    // Syntax: binarySearchBy(keyToFind) { selector }
                                    val index = users.binarySearchBy(105) { it.id }
                                    if (index >= 0) {
                                        println("User found: ${users[index]}")
                                    } else {
                                        println("User not found.")
                                    }
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "distinct() / distinctBy()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "distinct: The distinct function is the simplest way to remove duplicates. It returns a list containing only the unique elements from the original collection. It iterates through your collection and keeps an element only if it hasn’t been seen before. It relies on the natural equality of the objects (usually equals() and hashCode()). Use distinct when you are dealing with simple types (Strings, Ints, Enums) or objects where the default equality check is exactly what you need.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "distinctBy: Sometimes, “equality” isn’t black and white. What if you have a list of distinct objects, but you consider them duplicates based on a specific property? This is where distinctBy shines. It allows you to define a selector—a rule that tells the compiler, If two items have the same X, treat them as duplicates. Imagine you have a list of products. You want to ensure you don’t have multiple products with the same id, even if their prices or names slightly differ due to data glitches. distinctBy usually keeps the first element it encounters and discards subsequent duplicates. In the example above, the \"Sale\" version of the mouse was dropped because the original \"A100\" was found first.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                                 //distinct
                                 val numbers = listOf(1, 2, 2, 3, 4, 4, 5)
                                 val distinctNumbers = numbers.distinct()
                                 
                                 //distinctBy
                                 data class Product(val id: String, val name: String, val price: Double)

                                 val inventory = listOf(
                                     Product("A100", "Gaming Mouse", 50.0),
                                     Product("B200", "Mechanical Keyboard", 120.0),
                                     Product("A100", "Gaming Mouse (Sale)", 45.0) // Duplicate ID!
                                 )
                                 // distinct() wouldn't work here because the objects aren't identical.
                                 // distinctBy() saves the day.
                                 val uniqueInventory = inventory.distinctBy { it.id }
                                 println(uniqueInventory)
                            """.trimIndent()
            )

            SpanText(
                text = "distinct() / set()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li {
                    SpanText(
                        text = "distinct: distinct() is a function that removes duplicates from a collection and returns a new List. Duplicate handling: After creation",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li {
                    SpanText(
                        text = "set: Set is a collection type that cannot contain duplicates by design. Duplicate handling: At insertion",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "any()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, the any function is used to determine if at least one element in a collection satisfies a given condition or predicate. It returns true if any element meets the specified condition; otherwise, it returns false.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                fun main () {
                                    val numbers = listOf(1, 2, 3, 4, 5)
                                    val hasEvenNumber = numbers.any { it % 2 == 0 }

                                    println("Has even number:")
                                    println(hasEvenNumber)
                                    // Output: Has even number: true
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "none()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin programming language, the none() function is used to check whether none of the elements in a collection or an array satisfy a given condition. This function is commonly applied to collections like List, Set, and Map.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                fun main () {
                                    val numbers = listOf(1, 3, 5, 7, 9)
                                    val noneGreaterThanTen = numbers.none { it > 10 }
                                    println (noneGreaterThanTen)
                                }
                            """.trimIndent()
            )

            SpanText(
                text = "all()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "In Kotlin, the all() function is used to check if all elements in a collection satisfy a given condition. It is a higher-order function that takes a predicate as an argument and returns a Boolean value indicating whether all elements in the collection meet the specified condition.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    fun main () {
                        val numbers = listOf(2, 4, 6, 8, 10)
                        val allEven = numbers.all { it % 2 == 0 }
                        println(allEven) // Output: true
                    }
                """.trimIndent()
            )

            SpanText(
                text = "zipWithNext()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Such an analysis can be applied in various fields like financial data analysis, monitoring temperature changes, changes in performance metrics, etc. Using zipWithNext provides an effective and simple way to explore the relationships between such sequential data points.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                 fun main() {
                                     val dailyPrices = listOf(100.0, 102.5, 101.0, 105.0, 107.0)
                                     val dailyChanges = dailyPrices.zipWithNext { previous, next -> next - previous }
                                     println(dailyChanges)
                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "intersect()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "The intersect function in Kotlin is used to find the intersection between two collections. This means it identifies and returns the common elements present in both collections. This function is particularly useful in scenarios where you need to filter out elements that are shared across two datasets, such as finding shared interests, shared followers, or shared features between different sets of data.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                fun main () {
                                    // Contents recommended based on user's interests
                                    val recommendedContents = setOf("Sports", "Technology", "Music", "Cooking")
                                    // Contents that the user is following
                                    val followedContents = setOf( "Fashion", "Technology", "Cooking", "Art")
                                    // Finding the intersection of recommended and followed contents
                                    // This will identify contents that are both recommended to the user based on their interests
                                    // and are already being followed by them
                                    val commonContents = recommendedContents. intersect(followedContents)

                                    // Printing out the contents that are both recommended and followed
                                    println("Contents both recommended and followed:")
                                    println(commonContents)
                                }

                                //Contents both recommended and followed: [Technology, Cooking]
                            """.trimIndent()
            )

            SpanText(
                text = "runningFold()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The runningFold function can be utilized to perform cumulative data calculations based on user interactions or in-app events. It’s particularly useful for tracking user behavior, calculating total in-app purchases, or accumulating scores in a game scenario, among others. Here’s an example scenario in Android where runningFold could be effectively used.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "In this example, as each product is added to the cart, the cumulative total price is calculated using runningFold, and this total is presented to the user, updated at each step. This provides clear visual feedback to the user about the cost implications of each addition they make.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                                 fun main() {
                                     // Prices of products added to the cart by the user
                                     val productPrices = listOf (5.99, 15.99, 3.49, 2.99)
                                     // Using runningFold to calculate the cumulative total
                                     val cumulativeTotal = productPrices.runningFold(0.0) { total, price ->
                                         total + price
                                     }
                                     // Displaying the updated total to the user after each product addition
                                     cumulativeTotal.forEach { total ->
                                         println(%.2f".format(total))
                                     }
                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "coerceAtLeast(minValue), coerceIn(minValue, maxValue), coerceAtMax, coerceAtMost(maxValue)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                                 fun main(){
                                     val value = 12
                                     //val result = if(value > 0) value else 0

                                     val results = value.coerceAtLeast(0)
                                     print(results);
                                 }
                            """.trimIndent()
            )

            SpanText(
                text = "elementAt(), elementAtOrElse() (null-safety operators like), elementAtOrNull() (lambda function that defines what to return if that index doesn’t exist)",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Returns the element at a specified index or a default value if the index is out of bounds.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                                val list = listOf("Kotlin", "Java", "Swift")
                                println(list.elementAt(1)) // Output: Java
                                // println(list.elementAt(5)) // ❌ Throws Exception
                                
                                val list = listOf("Kotlin", "Java")
                                val result = list.elementAtOrNull(5) 
                                println(result) // Output: null
                                println(result ?: "Language not found") // Output: Language not found
                                
                                val numbers = listOf(1, 2, 3, 4, 5)
                                val elementAtIndex = numbers.elementAtOrElse(7) { -1 }
                                // Output: -1
                            """.trimIndent()
            )

            SpanText(
                text = "getOrElse() / getOrNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {
                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "Returns the element at the specified index or a default value if the index is out of bounds.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "While getOrNull and getOrElse are great for safely accessing optional elements that may or may not be present, other approaches are more appropriate when elements are mandatory. Specifically, if you are dealing with a collection where a particular item must be present, using a more explicit approach like check() or require() is better to enforce that requirement.",
                        modifier = Modifier.padding(bottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "The shipping address (first element) is mandatory and must always be present for order processing. he billing address (second element), if present, may differ from the shipping address. If it’s missing, it should default to the shipping address. In this case, we use require() to enforce the presence of a shipping address, and only use getOrElse to handle the optional billing address:",
                        modifier = Modifier.padding(bottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            KotlinCode(
                code = """
                    val addresses = arrayOf(Address("Shipping"))

                    // Ensure that the shipping address is always present
                    require(addresses.isNotEmpty()) { "Shipping address is required." }

                    // Access the shipping address directly, as we know it must exist
                    val shippingAddress = addresses[0]

                    // Use getOrElse for billing address, defaulting to delivery address if absent
                    val billingAddress = addresses.getOrElse(1) { shippingAddress }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    val numbers = listOf(1, 2, 3, 4, 5)
                    val element = numbers.getOrElse(7) { -1 }
                    // Output: -1getOrElse, getOrNull: Returns the element at the specified index or a default value if the index is out of bounds.

                    val numbers = listOf(1, 2, 3, 4, 5)
                    val element = numbers.getOrElse(7) { -1 }
                    // Output: -1
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    val contacts = arrayOf("Primary Email", "Phone Number")
                    val primaryEmail = contacts[0]
                    val secondaryEmail = if (contacts.size > 2) contacts[2] else null
                    println(secondaryEmail) // Prints: null if index is out of bounds

                    val secondaryEmail = contacts.getOrNull(2)
                    println(secondaryEmail) // Prints: null if index is out of bounds
                """.trimIndent()
            )

            SpanText(
                text = "sumOf()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            KotlinCode(
                code = """
                    fun main() {
                        print(calculateTotalPrice(listOf(100.0, 200.0)))
                    }
                    
                    fun calculateTotalPrice(products: List<Double>): Double {
                        return products.sumOf { it }
                    }
                    
                    //300.0
                """.trimIndent()
            )

            SpanText(
                text = "maxByOrNull() / minByOrNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "These functions return the element of the collection that yields the largest or smallest value when transformed by the given selector function.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                    //Max age or Salary
                     
                     data class Person(val name: String, val age: Int)

                     val people = listOf(
                        Person("Alice", 30),
                        Person("Bob", 25),
                        Person("Charlie", 35)
                     )
                     
                     val oldestPerson = people.maxByOrNull { it.age }
                     val youngestPerson = people.minByOrNull { it.age }
                """.trimIndent()
            )

            SpanText(
                text = "toInt() / toIntOrNull()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Ul {

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "toInt(): Use it when you're confident about your input data, as it eagerly converts a string to an integer.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }

                Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                    SpanText(
                        text = "toIntOrNull(): Opt for this safer alternative when working with potentially unreliable data sources. It diligently validates the string and returns null if the conversion fails, providing a safety net for your application.",
                        modifier = Modifier.padding(topBottom = 0.2.em)
                            .fontSize(FontSize.Medium)
                    )
                }
            }

            SpanText(
                text = "takeUnless()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "Returns this value if it does not satisfy the given predicate or null, if it does.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            KotlinCode(
                code = """
                     val age = 16.takeUnless { it < 18 } ?: throw IllegalArgumentException("Age must be 18+")
                """.trimIndent()
            )

            SpanText(
                text = "takeIf()",
                modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                    .fontWeight(FontWeight.Bold)
            )

            Li {
                SpanText(
                    text = "takeIf is a higher-order function that allows us to return the object itself if it meets a specified condition (predicate), or null if it doesn’t. This promotes a more functional style of programming and can lead to cleaner, more concise code.",
                    modifier = Modifier.padding(topBottom = 0.2.em)
                        .fontSize(FontSize.Medium)
                )
            }

            //https://medium.com/@sivavishnu0705/827e25ffe157?sk=f6e086b062c79af422223b39785106fa
            KotlinCode(
                code = """
                     //Expression-first pipeline
                     fun calculateShippingDiscount(cart: ShoppingCart): BigDecimal = 
                        cart.takeIf { it.itemCount >= 5 && it.totalWeightKg <= 20.0 }
                            ?.calculateBaseShippingCost()
                            ?.multiply(BigDecimal("0.50"))
                            ?: BigDecimal.ZERO
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun getArticle(userQuery: String? = null) { 
                        val safeQuery = userQuery?.takeIf { it.isNotBlank() }?.trimEnd() ?: ""
                        if (safeQuery.equals("", ignoreCase = true)
                    ) { }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    fun checkAge(age: Int): String? {
                        return age.takeIf { it >= 18 }?.let { "Kamlesh" } ?: "Minor"
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                     //Optimized Approach
                     
                     user?.takelf { it. isLoggedIn }
                        ?.let (dashboard:: showWelcomeMessage)
                        ?: dashboard.showLoginPrompt()
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Chain of takeIf may lead to null

                    val result = "Hello".takeIf { it.length > 3 }?.takeIf { it.startsWith("H") }
                    println(result) // Output: Hello
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Returning an object only when condition is met

                    fun findUser(name: String): User? {
                        return usersList.find { it.name == name }
                            ?.takeIf { it.isActive }
                    }
                """.trimIndent()
            )

            KotlinCode(
                code = """
                    //Avoiding nested if statements

                    val input = readLine()
                    val sanitizedInput = input?.takeIf { it.length in 3..10 }?.trim()
                    println(sanitizedInput ?: "Invalid input")
                """.trimIndent()
            )
        }

        KotlinCode(
            code = """
                fun calculateUsingToInt (value: String) {
                    val intValue = value.toInt()
                }

                fun calculateUsingToIntOrNull (value: String) {
                    val intValue = value.toIntOrNull() 
                    intValue?.let {
                        // perform your logic
                    }
                }
            """.trimIndent()
        )

        SpanText(
            text = "What does this print, and why?",
            modifier = Modifier.fontSize(FontSize.Large).padding(topBottom = 1.em)
                .fontWeight(FontWeight.Bold)
        )

        KotlinCode(
            code = """
                fun main() {
                    val list = listOf(1, 2, 3)
                   
                    val result = list.map { it * 2 }.filter { it > 2 }.first()
                    println(result)
                  
                    val seq = list.asSequence().map { it * 2 }.filter { it > 2 }.first()
                    println(seq)
                }
                """.trimIndent()
        )

        Ul {
            Li(Modifier.padding(topBottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "Both print 4, but the execution model differs completely.",
                    modifier = Modifier.padding(topBottom = 0.2.em).fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "The List version is eager — map creates a full intermediate list [2,4,6], then filter creates [4,6], then first() picks 4.",
                    modifier = Modifier.padding(topBottom = 0.2.em).fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "The Sequence version is lazy — elements flow one at a time through the chain: 1 → 2 → fails filter, 2 → 4 → passes → first() returns immediately. Item 3 is never processed.",
                    modifier = Modifier.padding(topBottom = 0.2.em).fontSize(FontSize.Medium)
                )
            }

            Li(Modifier.padding(bottom = 0.3.em).toAttrs()) {
                SpanText(
                    text = "sequences win for large collections with many chained operators or early termination; plain collections win for small lists (sequence overhead isn't free).",
                    modifier = Modifier.padding(topBottom = 0.2.em).fontSize(FontSize.Medium)
                )
            }
        }
    }
}

//https://www.youtube.com/watch?v=lxoKilLSJ4k
//padStart